import { useState } from "react";
import { Head, Link } from "@inertiajs/react";
import { Box, Stack, Group, Button, Image, Modal, Text } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import {
  IconArrowLeft,
  IconCalendar,
  IconMapPin,
  IconCamera,
  IconChevronLeft,
  IconChevronRight,
  IconX,
} from "@tabler/icons-react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import AppShell from "../../Components/Layout/AppShell";
import PageShell from "../../Components/Layout/PageShell";
import {
  Heading1,
  Heading2,
  DimmedText,
} from "../../Components/UI/Typography";
import { Badge } from "../../Components/UI/Badge";
import { getGigBySlug } from "../../data/gallery";

interface Props {
  slug: string;
}

export default function CaptureShow({ slug }: Props) {
  const gig = getGigBySlug(slug);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (!gig) {
    return (
      <AppShell>
        <Head>
          <title>Not Found | Ogarocious</title>
        </Head>
        <Box
          style={{
            minHeight: "100vh",
            background:
              "linear-gradient(180deg, #0a0705 0%, #0f0b08 50%, #0a0705 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack align="center" gap="md">
            <Heading1>Gig Not Found</Heading1>
            <DimmedText>
              That gallery doesn&apos;t exist or has been removed.
            </DimmedText>
            <Button variant="gradient" component={Link} href="/media">
              Back to Capture
            </Button>
          </Stack>
        </Box>
      </AppShell>
    );
  }

  const formattedDate = new Date(gig.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  return (
    <>
      <Head>
        <title>{`${gig.title} | Capture | Ogarocious`}</title>
        <meta
          name="description"
          content={
            gig.description ||
            `${gig.photos.length} photos from ${gig.title} at ${gig.venue}.`
          }
        />
        <meta
          property="og:title"
          content={`${gig.title} | Capture | Ogarocious`}
        />
        <meta
          property="og:description"
          content={
            gig.description ||
            `${gig.photos.length} photos from ${gig.title} at ${gig.venue}.`
          }
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`${import.meta.env.VITE_SITE_URL || ""}/media/${gig.slug}`}
        />
        <meta
          property="og:image"
          content={gig.coverImage || import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${gig.title} | Capture | Ogarocious`}
        />
        <meta
          name="twitter:description"
          content={gig.description || `${gig.photos.length} photos from ${gig.title}.`}
        />
        <meta
          name="twitter:image"
          content={gig.coverImage || import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
      </Head>

      <PageShell variant="content" containerSize="xl">
          <Stack gap={40}>
            {/* ── Back ── */}
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconArrowLeft size={16} />}
              component={Link}
              href="/media"
              style={{
                color: "rgba(161, 161, 170, 0.7)",
                alignSelf: "flex-start",
              }}
            >
              Back to Capture
            </Button>

            {/* ── Gig Header ── */}
            <Stack gap="md">
              <Group gap="xs">
                {gig.tags.map((tag) => (
                  <Badge key={tag} variant="light" size="sm" radius="md">
                    {tag}
                  </Badge>
                ))}
              </Group>

              <Heading1 variant="shadow">{gig.title}</Heading1>

              <Group gap="xl" wrap="wrap">
                <Group gap="xs">
                  <IconCalendar
                    size={15}
                    style={{ color: "rgba(161, 161, 170, 0.5)" }}
                  />
                  <DimmedText size="sm">{formattedDate}</DimmedText>
                </Group>
                <Group gap="xs">
                  <IconMapPin
                    size={15}
                    style={{ color: "rgba(161, 161, 170, 0.5)" }}
                  />
                  <DimmedText size="sm">
                    {gig.venue}
                    {gig.location ? ` · ${gig.location}` : ""}
                  </DimmedText>
                </Group>
                <Group gap="xs">
                  <IconCamera
                    size={15}
                    style={{ color: "rgba(161, 161, 170, 0.5)" }}
                  />
                  <DimmedText size="sm">{gig.photos.length} photos</DimmedText>
                </Group>
              </Group>

              {gig.description && (
                <DimmedText size="md" style={{ maxWidth: 680, lineHeight: 1.7 }}>
                  {gig.description}
                </DimmedText>
              )}
            </Stack>

            {/* ── Masonry Photo Grid ── */}
            <Box>
              <Heading2 mb="lg">Photos</Heading2>
              <ResponsiveMasonry
                columnsCountBreakPoints={{ 350: 1, 640: 2, 1024: 3 }}
              >
                <Masonry gutter="12px">
                  {gig.photos.map((photo, index) => (
                    <Box
                      key={index}
                      onClick={() => openLightbox(index)}
                      style={{
                        overflow: "hidden",
                        borderRadius: "var(--mantine-radius-md)",
                        cursor: "pointer",
                        position: "relative",
                      }}
                    >
                      <Image
                        src={photo.url}
                        alt={photo.caption || `${gig.title} — photo ${index + 1}`}
                        fit="cover"
                        style={{
                          width: "100%",
                          display: "block",
                          transition: "transform 0.35s ease",
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLImageElement).style.transform =
                            "scale(1.04)";
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLImageElement).style.transform =
                            "scale(1)";
                        }}
                      />
                      {photo.caption && (
                        <Box
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.75), transparent)",
                            padding: "0.75rem 1rem 0.5rem",
                          }}
                        >
                          <Text
                            size="xs"
                            style={{ color: "rgba(255,255,255,0.85)" }}
                          >
                            {photo.caption}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Masonry>
              </ResponsiveMasonry>
            </Box>
          </Stack>
      </PageShell>

      {/* ── Lightbox Modal ── */}
        <Modal
          opened={lightboxIndex !== null}
          onClose={closeLightbox}
          size="xl"
          centered
          withCloseButton={false}
          styles={{
            content: {
              backgroundColor: "rgba(10, 10, 11, 0.98)",
              border: "1px solid rgba(55, 58, 64, 0.4)",
            },
            body: { padding: 0 },
          }}
        >
          {lightboxIndex !== null && (
            <Box style={{ position: "relative" }}>
              <Button
                variant="subtle"
                color="gray"
                onClick={closeLightbox}
                leftSection={<IconX size={16} />}
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  zIndex: 10,
                  background: "rgba(0,0,0,0.6)",
                  backdropFilter: "blur(8px)",
                }}
              >
                Close
              </Button>

              <Carousel
                initialSlide={lightboxIndex}
                onSlideChange={(idx) => setLightboxIndex(idx)}
                withIndicators
                withKeyboardEvents
                previousControlIcon={<IconChevronLeft size={20} />}
                nextControlIcon={<IconChevronRight size={20} />}
                styles={{
                  indicator: {
                    backgroundColor: "rgba(232, 93, 60, 0.4)",
                    "&[dataActive]": { backgroundColor: "#e85d3c" },
                  },
                  control: {
                    backgroundColor: "rgba(0,0,0,0.6)",
                    border: "1px solid rgba(245, 238, 224, 0.15)",
                    color: "#f5eee0",
                  },
                }}
              >
                {gig.photos.map((photo, index) => (
                  <Carousel.Slide key={index}>
                    <Image
                      src={photo.url}
                      alt={
                        photo.caption ||
                        `${gig.title} — photo ${index + 1}`
                      }
                      fit="contain"
                      style={{ maxHeight: "75vh", width: "100%" }}
                    />
                    {photo.caption && (
                      <Box
                        style={{
                          textAlign: "center",
                          padding: "0.75rem 1rem",
                          background: "rgba(0,0,0,0.4)",
                        }}
                      >
                        <DimmedText size="sm">{photo.caption}</DimmedText>
                      </Box>
                    )}
                  </Carousel.Slide>
                ))}
              </Carousel>
            </Box>
          )}
      </Modal>
    </>
  );
}
